// SAFE MODE: delivery sessions improvements
// External delivery page — /delivery/:token
// Flow: phone entry → OTP verification → professional gallery + ZIP download

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  MessageSquare, Shield, Download, ChevronRight, ChevronLeft,
  X, Star, Send, Image, Film, HardDrive, Calendar, CheckCircle2,
  AlertCircle, ArrowRight, RotateCcw, Lock, AlertTriangle,
} from 'lucide-react';
import {
  sendDeliveryOtp,
  verifyDeliveryOtp,
  getDeliveryFiles,
  getDeliveryDownloadUrls,
  submitDeliveryRating,
  submitDeliveryDispute,
} from '../services/deliveryPageService';
import { buildDeliveryZipBlob } from '../utils/deliveryClientZip';
import { getPhotographyTypeLabelAr } from '../utils/bookingLabels';
import MashhadLogo from '../components/MashhadLogo';
import OtpInput from '../components/OtpInput';

// ─── helpers ────────────────────────────────────────────────────────────────
const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024, s = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + s[i];
};
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
const addDays = (dateStr, days) => { const d = new Date(dateStr); d.setDate(d.getDate() + days); return d; };
const daysRemaining = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((addDays(dateStr, 30) - Date.now()) / 86400000);
};

// ─── Step indicator (shared) ─────────────────────────────────────────────────
const StepIndicator = ({ current }) => (
  <div className="flex items-center gap-1 mb-8" dir="ltr">
    {['رقم الجوال', 'التحقق', 'الألبوم'].map((label, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <React.Fragment key={i}>
          <div className="flex flex-col items-center gap-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all
              ${done ? 'bg-emerald-500 text-white' : active ? 'bg-primary text-white shadow-lg shadow-primary/30' : 'bg-slate-100 text-slate-400'}`}>
              {done ? <CheckCircle2 size={16} /> : i + 1}
            </div>
            <span className={`text-[10px] font-medium ${active ? 'text-primary' : done ? 'text-emerald-500' : 'text-slate-400'}`}>
              {label}
            </span>
          </div>
          {i < 2 && <div className={`flex-1 h-0.5 mb-4 transition-all ${done ? 'bg-emerald-400' : 'bg-slate-200'}`} />}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Expiry Banner ───────────────────────────────────────────────────────────
const ExpiryBanner = ({ deliveredAt }) => {
  const days = daysRemaining(deliveredAt);
  if (days === null) return null;
  const expDate = formatDate(addDays(deliveredAt, 30).toISOString());
  const urgent = days <= 7, warning = days <= 14;
  return (
    <div className={`rounded-2xl px-4 py-3 text-sm flex items-start gap-3 ${
      urgent ? 'bg-red-50 border border-red-200 text-red-700'
      : warning ? 'bg-amber-50 border border-amber-200 text-amber-700'
      : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
      <AlertCircle size={16} className="mt-0.5 shrink-0" />
      <span>
        سيتم حذف الملفات نهائياً بتاريخ <strong>{expDate}</strong>
        {days > 0 ? ` — ${days} يوم متبقي` : ' — آخر يوم!'}
      </span>
    </div>
  );
};

// ─── Auth card wrapper ───────────────────────────────────────────────────────
// SAFE MODE: footer-matched logo (logoType footer + xl) and horizontally centered
const AuthCard = ({ children }) => (
  <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-10">
    <div className="w-full max-w-sm">
      <div className="mb-6 w-full flex justify-center items-center">
        <MashhadLogo variant="light" size="xl" logoType="footer" />
      </div>
      <div className="bg-white rounded-3xl shadow-2xl p-7" dir="rtl">
        {children}
      </div>
    </div>
  </div>
);

// ─── Step 1 — Phone entry ────────────────────────────────────────────────────
const PhoneStep = ({ token, onSuccess }) => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!phone.trim()) { setError('يرجى إدخال رقم الجوال'); return; }
    setLoading(true);
    try {
      const res = await sendDeliveryOtp(token, phone.trim());
      if (res.ok) onSuccess(phone.trim(), res.msegatId);
      else setError(res.message || 'حدث خطأ، حاول مرة أخرى');
    } catch { setError('تعذّر الاتصال بالخادم، تحقق من الإنترنت'); }
    finally { setLoading(false); }
  };

  return (
    <AuthCard>
      <StepIndicator current={0} />
      <div className="text-center mb-6">
        <h1 className="text-xl font-bold text-slate-900">استلام الألبوم</h1>
        <p className="text-slate-500 text-sm mt-1">أدخل رقم جوالك للوصول إلى صورك</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">رقم الجوال</label>
          <div className="relative">
            <input
              type="tel" dir="ltr" value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="05xxxxxxxx"
              className="w-full border-2 border-slate-200 focus:border-primary rounded-2xl px-4 py-3.5 text-center text-lg font-mono tracking-widest focus:outline-none transition"
              autoFocus
            />
          </div>
        </div>
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={15} className="shrink-0" />
            {error}
          </div>
        )}
        <button type="submit" disabled={loading}
          className="w-full bg-primary hover:opacity-90 disabled:opacity-50 text-white font-bold py-3.5 rounded-2xl transition flex items-center justify-center gap-2 shadow-lg shadow-primary/25">
          {loading
            ? <><span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الإرسال...</>
            : <><Send size={16} />إرسال رمز التحقق</>}
        </button>
      </form>
      <div className="flex items-center gap-2 mt-5 text-slate-400 text-xs justify-center">
        <Lock size={11} />
        <span>الوصول محمي  </span>
      </div>
    </AuthCard>
  );
};

// ─── Step 2 — OTP entry ──────────────────────────────────────────────────────
const OTPStep = ({ token, phone, msegatId: initialMsegatId, onSuccess, onBack }) => {
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(60);
  const [currentMsegatId, setCurrentMsegatId] = useState(initialMsegatId);
  const verifyInFlightRef = useRef(false);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const handleVerify = useCallback(async (codeOverride) => {
    const code = codeOverride ?? otpCode;
    if (code.length < 4) { setError('أدخل الكود كاملاً (4 أرقام)'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await verifyDeliveryOtp(token, phone, code, currentMsegatId);
      if (res.ok && res.access_token) onSuccess(res.access_token);
      else {
        setError(res.message || 'الكود غير صحيح');
        setOtpCode('');
      }
    } catch { setError('تعذّر الاتصال بالخادم'); }
    finally { setLoading(false); }
  }, [otpCode, token, phone, currentMsegatId, onSuccess]);

  const handleOtpComplete = useCallback(
    (code) => {
      if (loading || verifyInFlightRef.current) return;
      verifyInFlightRef.current = true;
      handleVerify(code).finally(() => {
        verifyInFlightRef.current = false;
      });
    },
    [loading, handleVerify]
  );

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    const res = await sendDeliveryOtp(token, phone);
    if (res.ok) {
      setCurrentMsegatId(res.msegatId);
      setResendCooldown(60);
      setOtpCode('');
    } else setError(res.message || 'فشل إعادة الإرسال');
  };

  return (
    <AuthCard>
      <StepIndicator current={1} />
      <button onClick={onBack} className="flex items-center gap-1 text-slate-400 hover:text-slate-600 text-sm mb-5 transition">
        <ArrowRight size={15} /> العودة
      </button>
      <div className="text-center mb-7">
        <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <MessageSquare size={24} className="text-primary" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">رمز التحقق</h2>
        <p className="text-slate-500 text-sm mt-1.5">
          أُرسل رمز مكوّن من 4 أرقام إلى<br />
          <span className="font-mono font-semibold text-slate-700 text-base mt-1 block" dir="ltr">{phone}</span>
        </p>
      </div>

      <div className="mb-5">
        <OtpInput
          value={otpCode}
          onChange={setOtpCode}
          onComplete={handleOtpComplete}
          disabled={loading}
          autoFocus
          className="flex gap-3 justify-center"
          boxClassName="w-14 h-16 text-center text-2xl font-bold border-2 rounded-2xl focus:outline-none transition border-slate-200 text-slate-900 focus:border-primary focus:shadow-lg focus:shadow-primary/10"
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 text-sm mb-4">
          <AlertCircle size={15} className="shrink-0" />{error}
        </div>
      )}

      <button onClick={() => handleVerify()} disabled={loading || otpCode.length < 4}
        className="w-full bg-primary hover:opacity-90 disabled:opacity-40 text-white font-bold py-3.5 rounded-2xl transition flex items-center justify-center gap-2 shadow-lg shadow-primary/25 mb-4">
        {loading
          ? <><span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري التحقق...</>
          : <><Shield size={16} />تحقق ودخول</>}
      </button>

      <div className="text-center text-sm">
        {resendCooldown > 0 ? (
          <span className="text-slate-400 flex items-center justify-center gap-1.5">
            <RotateCcw size={13} />إعادة الإرسال خلال {resendCooldown}ث
          </span>
        ) : (
          <button onClick={handleResend} className="text-primary hover:underline font-semibold flex items-center justify-center gap-1.5 mx-auto">
            <RotateCcw size={13} />إعادة إرسال الرمز
          </button>
        )}
      </div>
    </AuthCard>
  );
};

// ─── Lightbox ─────────────────────────────────────────────────────────────────
// fullUrls: array of { url } indexed by sort order (same as files array), or null while loading
const Lightbox = ({ files, currentIndex, thumbUrls, fullUrls, onClose, onPrev, onNext, onJump }) => {
  const file = files[currentIndex];
  const thumbUrl = thumbUrls[file?.id];
  const fullUrl = fullUrls?.[currentIndex]?.url ?? null;
  const isVideo = file?.file_type === 'video';

  const [imgReady, setImgReady] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // Detect touch/mouse device once
  useEffect(() => {
    setIsTouch(window.matchMedia('(pointer: coarse)').matches);
  }, []);

  // Reset imgReady when navigating to a different item
  useEffect(() => {
    setImgReady(false);
  }, [currentIndex]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onNext();
      if (e.key === 'ArrowRight') onPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    const dy = Math.abs((touchStartY.current ?? 0) - e.changedTouches[0].clientY);
    if (Math.abs(dx) > 50 && Math.abs(dx) > dy) { dx > 0 ? onNext() : onPrev(); }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < files.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black flex flex-col select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onContextMenu={e => e.preventDefault()}
    >
      {/* ── Top bar ── */}
      <div className="shrink-0 flex items-center justify-between px-4 py-3 z-10"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, transparent 100%)' }}>
        <button
          onClick={onClose}
          className="w-11 h-11 rounded-full flex items-center justify-center text-white transition active:scale-90"
          style={{ background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(8px)' }}
          aria-label="إغلاق"
        >
          <X size={22} />
        </button>
        <div className="text-white text-sm font-mono bg-black/40 px-4 py-1.5 rounded-full" dir="ltr">
          {currentIndex + 1} / {files.length}
        </div>
        <div className="w-11" />
      </div>

      {/* ── Media area ── */}
      <div className="flex-1 flex items-center justify-center relative min-h-0 px-2">
        {/* Tap backdrop to close */}
        <div className="absolute inset-0" onClick={onClose} />

        {/* Prev arrow — desktop (mouse) only; on touch devices swipe is used */}
        {hasPrev && !isTouch && (
          <button
            onClick={e => { e.stopPropagation(); onPrev(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center text-white transition active:scale-90"
            style={{ background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(8px)' }}
          >
            <ChevronRight size={26} />
          </button>
        )}
        {/* Next arrow — desktop (mouse) only */}
        {hasNext && !isTouch && (
          <button
            onClick={e => { e.stopPropagation(); onNext(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center text-white transition active:scale-90"
            style={{ background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(8px)' }}
          >
            <ChevronLeft size={26} />
          </button>
        )}

        {/* Media container */}
        <div
          className="relative z-[1] flex items-center justify-center w-full h-full"
          style={{ padding: isTouch ? '0 0.5rem' : '0 3.5rem' }}
        >
          {isVideo ? (
            fullUrl ? (
              /* Full-res video — plays correctly */
              <video
                src={fullUrl} controls autoPlay
                className="max-w-full max-h-full rounded-xl shadow-2xl"
                onClick={e => e.stopPropagation()}
                onContextMenu={e => e.preventDefault()}
              />
            ) : (
              /* Video URL not yet fetched — show blurred thumbnail + spinner */
              <div className="flex flex-col items-center justify-center gap-4 p-8">
                {thumbUrl && (
                  <img src={thumbUrl} alt="" draggable={false}
                    className="max-w-full rounded-xl object-contain opacity-40"
                    style={{ maxHeight: '50vh', filter: 'blur(6px)', WebkitTouchCallout: 'none' }}
                  />
                )}
                <div className="flex items-center gap-3 text-white bg-black/60 px-5 py-3 rounded-full backdrop-blur-sm">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0" />
                  <span className="text-sm font-medium">جاري تحميل الفيديو…</span>
                </div>
              </div>
            )
          ) : (
            /* Image — progressive: thumbnail shown immediately, full-res fades in */
            <div
              className="relative flex items-center justify-center w-full h-full"
              onClick={e => e.stopPropagation()}
            >
              {/* Thumbnail: visible until full is ready, slightly blurred when full is loading */}
              {thumbUrl && (
                <img
                  src={thumbUrl} alt="" draggable={false}
                  className={`max-w-full max-h-full rounded-xl object-contain absolute inset-0 m-auto transition-opacity duration-500
                    ${imgReady ? 'opacity-0' : 'opacity-100'}
                    ${fullUrl && !imgReady ? 'blur-[2px]' : ''}`}
                  onContextMenu={e => e.preventDefault()}
                  style={{ WebkitTouchCallout: 'none' }}
                />
              )}
              {/* Full-resolution: fades in once loaded */}
              {fullUrl && (
                <img
                  key={fullUrl}
                  src={fullUrl} alt="" draggable={false}
                  className={`max-w-full max-h-full rounded-xl shadow-2xl object-contain relative transition-opacity duration-500
                    ${imgReady ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setImgReady(true)}
                  onContextMenu={e => e.preventDefault()}
                  style={{ WebkitTouchCallout: 'none' }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Thumbnail strip ── */}
      {files.length > 1 && (
        <div
          className="shrink-0 pt-3 pb-4 px-4"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)' }}
        >
          <div className="flex gap-2 overflow-x-auto justify-center" style={{ scrollbarWidth: 'none' }}>
            {files.map((f, i) => (
              <button
                key={f.id}
                onClick={() => onJump(i)}
                className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all ${
                  i === currentIndex
                    ? 'ring-2 ring-white ring-offset-1 ring-offset-black opacity-100 scale-105'
                    : 'opacity-50 hover:opacity-80'
                }`}
              >
                {thumbUrls[f.id] ? (
                  <img src={thumbUrls[f.id]} alt="" className="w-full h-full object-cover" draggable={false} />
                ) : (
                  <div className="w-full h-full bg-white/10 flex items-center justify-center">
                    {f.file_type === 'video'
                      ? <Film size={16} className="text-white/60" />
                      : <Image size={16} className="text-white/60" />}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Gallery item ────────────────────────────────────────────────────────────
const GalleryItem = React.memo(({ file, thumbUrl, index, onClick }) => (
  <div
    className="aspect-square rounded-xl overflow-hidden bg-slate-200 cursor-pointer relative group select-none shadow-sm hover:shadow-lg transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
    onClick={() => onClick(index)}
    onContextMenu={e => e.preventDefault()}
    style={{ WebkitTouchCallout: 'none' }}
  >
    {thumbUrl ? (
      <img
        src={thumbUrl} alt="" loading="lazy" draggable={false}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        onContextMenu={e => e.preventDefault()}
      />
    ) : (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-slate-100">
        {file.file_type === 'video'
          ? <Film size={30} className="text-slate-400" />
          : <Image size={30} className="text-slate-400" />}
        <span className="text-[10px] text-slate-400">{file.file_type === 'video' ? 'فيديو' : 'صورة'}</span>
      </div>
    )}

    {/* Video badge */}
    {file.file_type === 'video' && (
      <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm">
        <Film size={10} />
        <span>فيديو</span>
      </div>
    )}

    {/* Play button overlay for videos */}
    {file.file_type === 'video' && (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20 group-hover:bg-black/70 transition">
          <div className="w-0 h-0 border-l-[12px] border-l-white border-y-[7px] border-y-transparent ml-1" />
        </div>
      </div>
    )}

    {/* Hover overlay */}
    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-200" />
  </div>
));

// ─── Star rating component ────────────────────────────────────────────────────
const StarRating = ({ rating, onRate, size = 32 }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1 justify-center" dir="ltr">
      {[1, 2, 3, 4, 5].map((s) => (
        <button key={s} type="button"
          onClick={() => onRate(s)}
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(0)}
          className="transition-transform hover:scale-110 active:scale-95">
          <Star size={size}
            className={`transition-colors ${s <= (hover || rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
        </button>
      ))}
    </div>
  );
};

// ─── Rating Modal ─────────────────────────────────────────────────────────────
const RatingModal = ({ accessToken, onClose }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  // 'form' | 'done' | 'already'
  const [state, setState] = useState('form');
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (rating === 0) { setError('يرجى اختيار تقييم'); return; }
    setError(''); setLoading(true);
    try {
      const res = await submitDeliveryRating(accessToken, rating, comment);
      if (res.ok) {
        setState('done');
      } else if (res.message?.includes('مسبقاً')) {
        setState('already');
      } else {
        setError(res.message || 'فشل إرسال التقييم');
      }
    } catch { setError('تعذّر الاتصال بالخادم'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" dir="rtl">
      <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-7" onClick={e => e.stopPropagation()}>

        {/* Already rated */}
        {state === 'already' && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star size={30} className="text-amber-500 fill-amber-500" />
            </div>
            <h3 className="font-bold text-slate-900 text-lg mb-2">قيّمت هذا المصور مسبقاً</h3>
            <p className="text-slate-500 text-sm mb-5">شكراً! تقييمك السابق محفوظ لدينا</p>
            <button onClick={onClose}
              className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 rounded-2xl transition">
              إغلاق
            </button>
          </div>
        )}

        {/* Success */}
        {state === 'done' && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-emerald-500" />
            </div>
            <h3 className="font-bold text-slate-900 text-lg mb-2">شكراً على تقييمك!</h3>
            <p className="text-slate-500 text-sm mb-5">تقييمك يساعدنا في تحسين الخدمة</p>
            <button onClick={onClose}
              className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 rounded-2xl transition">
              إغلاق
            </button>
          </div>
        )}

        {/* Form */}
        {state === 'form' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-slate-900 text-lg">تقييم المصور</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition">
                <X size={20} />
              </button>
            </div>
            <div className="mb-5">
              <p className="text-slate-500 text-sm text-center mb-4">كيف كانت تجربتك؟</p>
              <StarRating rating={rating} onRate={setRating} size={36} />
              {rating > 0 && (
                <p className="text-center text-sm mt-2 text-amber-500 font-medium">
                  {['', 'ضعيف', 'مقبول', 'جيد', 'جيد جداً', 'ممتاز'][rating]}
                </p>
              )}
            </div>
            <textarea
              value={comment} onChange={e => setComment(e.target.value)}
              placeholder="أضف تعليقاً (اختياري)..."
              rows={3}
              className="w-full border-2 border-slate-200 focus:border-primary rounded-2xl px-4 py-3 text-sm resize-none focus:outline-none transition mb-4"
            />
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 rounded-xl px-3 py-2.5 text-sm mb-4">
                <AlertCircle size={14} className="shrink-0" />{error}
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={onClose}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold py-3 rounded-2xl transition text-sm">
                إلغاء
              </button>
              <button onClick={handleSubmit} disabled={loading || rating === 0}
                className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-white font-bold py-3 rounded-2xl transition text-sm flex items-center justify-center gap-2">
                {loading
                  ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <><Star size={14} className="fill-white" />إرسال التقييم</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Step 3 — Gallery ────────────────────────────────────────────────────────
const GalleryStep = ({ accessToken }) => {
  const [data, setData] = useState(null);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');
  const [showRating, setShowRating] = useState(false);
  // Full-resolution URLs fetched lazily when lightbox first opens
  // null = not started, [] = failed/empty, array of { url } = ready (same order as files)
  const [fullUrls, setFullUrls] = useState(null);
  const fetchingFullUrls = useRef(false);
  // Dispute states
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeType, setDisputeType] = useState('');
  const [disputeMessage, setDisputeMessage] = useState('');
  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [disputeResult, setDisputeResult] = useState(null); // null | { ok, ticket_number, message }

  const fetchFullUrls = useCallback(async () => {
    if (fetchingFullUrls.current) return;
    fetchingFullUrls.current = true;
    try {
      const meta = await getDeliveryDownloadUrls(accessToken, 'all');
      if (meta.ok && meta.files?.length) {
        setFullUrls(meta.files.map(f => ({ url: f.signed_url })));
      } else {
        setFullUrls([]);
      }
    } catch {
      setFullUrls([]);
    }
  }, [accessToken]);

  const handleOpenLightbox = useCallback((index) => {
    setLightboxIndex(index);
    if (!fetchingFullUrls.current) fetchFullUrls();
  }, [fetchFullUrls]);

  const handleSubmitDispute = async () => {
    if (!disputeType || !disputeMessage.trim()) return;
    setSubmittingDispute(true);
    try {
      const res = await submitDeliveryDispute(accessToken, disputeType, disputeMessage.trim());
      setDisputeResult(res);
      if (res.ok || res.already_submitted) {
        setShowDisputeForm(false);
      }
    } catch {
      setDisputeResult({ ok: false, message: 'تعذّر الاتصال بالخادم، حاول مرة أخرى' });
    } finally {
      setSubmittingDispute(false);
    }
  };

  useEffect(() => {
    getDeliveryFiles(accessToken)
      .then(res => { if (res.ok) setData(res); else setError(res.message || 'فشل تحميل بيانات الألبوم'); })
      .catch(() => setError('تعذّر الاتصال بالخادم'))
      .finally(() => setLoadingGallery(false));
  }, [accessToken]);

  const thumbUrls = {};
  (data?.files ?? []).forEach(f => { thumbUrls[f.id] = f.thumbnail_url; });

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadProgress('جاري جلب روابط التحميل...');
    try {
      const meta = await getDeliveryDownloadUrls(accessToken, 'all');
      if (!meta.ok || !meta.files?.length) {
        setDownloadProgress(meta.message || 'فشل التحميل');
        setTimeout(() => setDownloadProgress(''), 5000);
        return;
      }
      setDownloadProgress('جاري ضغط الألبوم على جهازك...');
      const blob = await buildDeliveryZipBlob(meta.files, ({ label }) => {
        setDownloadProgress(label);
      });
      const fileName = 'mashhad-album-full.zip';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDownloadProgress('');
      // بعد اكتمال التحميل — اعرض نافذة التقييم تلقائياً إذا لم يقيّم بعد
      setShowRating(true);
    } catch (e) {
      setDownloadProgress(e?.message || 'تعذّر التحميل، حاول مرة أخرى');
      setTimeout(() => setDownloadProgress(''), 5000);
    } finally {
      setDownloading(false);
    }
  };

  if (loadingGallery) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-500 text-sm">جاري تحميل الألبوم...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" dir="rtl">
      <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle size={28} className="text-red-500" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">تعذّر تحميل الألبوم</h2>
        <p className="text-slate-500 text-sm">{error}</p>
      </div>
    </div>
  );

  const { delivery, files } = data;
  const title = getPhotographyTypeLabelAr(delivery?.photography_type) || 'جلسة تصوير';
  const date = formatDate(delivery?.booking_date);
  const delivered = formatDate(delivery?.delivered_at);

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {/* ── Sticky header — brand + session info only ── */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <MashhadLogo variant="dark" size="sm" />
          <div className="text-right min-w-0">
            <p className="text-xs font-semibold text-slate-700 truncate">{title}</p>
            <p className="text-[11px] text-slate-400">{date || delivered}</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5 space-y-4">
        {/* Expiry banner */}
        <ExpiryBanner deliveredAt={delivery?.delivered_at} />

        {/* Stats bar */}
        <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 flex flex-wrap gap-5 items-center text-sm">
          <div className="flex items-center gap-2 text-slate-700">
            <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
              <Image size={15} className="text-slate-500" />
            </div>
            <div><span className="font-bold text-slate-900">{delivery?.total_photos ?? 0}</span><span className="text-slate-500 mr-1">صورة</span></div>
          </div>
          <div className="flex items-center gap-2 text-slate-700">
            <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
              <Film size={15} className="text-slate-500" />
            </div>
            <div><span className="font-bold text-slate-900">{delivery?.total_videos ?? 0}</span><span className="text-slate-500 mr-1">فيديو</span></div>
          </div>
          <div className="flex items-center gap-2 text-slate-700">
            <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
              <HardDrive size={15} className="text-slate-500" />
            </div>
            <div><span className="font-bold text-slate-900">{formatSize(delivery?.total_size_bytes)}</span><span className="text-slate-500 mr-1">الحجم</span></div>
          </div>
          {delivered && (
            <div className="flex items-center gap-2 text-slate-400 mr-auto">
              <Calendar size={14} />
              <span className="text-xs">{delivered}</span>
            </div>
          )}
        </div>

        {/* ── Gallery grid — أولاً حتى يرى العميل الصور قبل اتخاذ أي قرار ── */}
        {files?.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
            {files.map((file, idx) => (
              <GalleryItem key={file.id} file={file} thumbUrl={thumbUrls[file.id]} index={idx} onClick={handleOpenLightbox} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Image size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm">لا توجد ملفات في هذا الألبوم</p>
          </div>
        )}

        {/* ── Action buttons — بعد مشاهدة الصور ── */}
        <div className="space-y-3">
          {/* زر التحميل — أساسي عرض كامل */}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="w-full flex items-center justify-center gap-2 bg-primary hover:opacity-90 disabled:opacity-60 text-white font-bold py-4 rounded-2xl transition shadow-lg shadow-primary/25 text-base"
          >
            {downloading
              ? <><span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري التجهيز...</>
              : <><Download size={18} />تحميل الألبوم كاملاً</>}
          </button>

          {downloadProgress && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5 text-sm text-primary text-center font-medium">
              {downloadProgress}
            </div>
          )}

          {/* زرا التقييم والمشكلة — في صف واحد */}
          <div className="flex gap-3">
            <button
              onClick={() => setShowRating(true)}
              className="flex-1 flex items-center justify-center gap-2 border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 font-bold py-3.5 rounded-2xl transition text-sm"
            >
              <Star size={16} className="fill-amber-400 text-amber-400" />
              قيّم المصور
            </button>
            <button
              onClick={() => { setShowDisputeForm(true); setDisputeResult(null); }}
              className="flex-1 flex items-center justify-center gap-2 border border-red-200 bg-red-50/60 hover:bg-red-50 text-red-600 font-medium py-3.5 rounded-2xl transition text-sm"
            >
              <AlertTriangle size={15} />
              يوجد مشكلة
            </button>
          </div>

          {/* نموذج الإبلاغ عن مشكلة */}
          {showDisputeForm && !disputeResult?.ok && !disputeResult?.already_submitted && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-red-800 text-sm flex items-center gap-2">
                  <AlertTriangle size={15} />
                  إبلاغ عن مشكلة في التسليم
                </h4>
                <button onClick={() => setShowDisputeForm(false)} className="text-red-400 hover:text-red-600 transition">
                  <X size={16} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-2">
                {[
                  { value: 'missing_photos', label: 'صور ناقصة أو أقل من المتفق عليه' },
                  { value: 'low_quality', label: 'جودة الصور سيئة أو غير مقبولة' },
                  { value: 'wrong_editing', label: 'التعديل لم يتم على الصور' },
                  { value: 'other', label: 'مشكلة أخرى' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDisputeType(opt.value)}
                    className={`text-right text-sm px-3 py-2.5 rounded-xl border transition-colors ${
                      disputeType === opt.value
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-red-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <textarea
                value={disputeMessage}
                onChange={(e) => setDisputeMessage(e.target.value)}
                placeholder="اشرح المشكلة بالتفصيل..."
                rows={3}
                className="w-full border border-red-200 bg-white rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
              />

              {disputeResult?.ok === false && (
                <div className="flex items-center gap-2 text-red-700 bg-red-100 border border-red-200 rounded-xl px-3 py-2.5 text-sm">
                  <AlertCircle size={14} className="shrink-0" />
                  {disputeResult.message}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowDisputeForm(false)}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition bg-white"
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  onClick={handleSubmitDispute}
                  disabled={submittingDispute || !disputeType || !disputeMessage.trim()}
                  className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-bold text-sm transition flex items-center justify-center gap-2"
                >
                  {submittingDispute
                    ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <><Send size={13} />إرسال الشكوى</>}
                </button>
              </div>
            </div>
          )}

          {/* تأكيد الإرسال الناجح */}
          {(disputeResult?.ok || disputeResult?.already_submitted) && (
            <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-2xl px-4 py-3.5 text-sm text-green-800">
              <CheckCircle2 size={17} className="text-green-500 shrink-0 mt-0.5" />
              <span>
                {disputeResult.already_submitted
                  ? `أبلغت عن مشكلة في هذا التسليم مسبقاً — رقم التذكرة: ${disputeResult.ticket_number}`
                  : `تم إرسال شكواك بنجاح${disputeResult.ticket_number ? ` — رقم التذكرة: ${disputeResult.ticket_number}` : ''}. سيتواصل معك فريق الدعم قريباً.`}
              </span>
            </div>
          )}
        </div>
      </main>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          files={files}
          currentIndex={lightboxIndex}
          thumbUrls={thumbUrls}
          fullUrls={fullUrls}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex(i => Math.max(0, i - 1))}
          onNext={() => setLightboxIndex(i => Math.min(files.length - 1, i + 1))}
          onJump={(i) => setLightboxIndex(i)}
        />
      )}

      {/* Rating modal */}
      {showRating && <RatingModal accessToken={accessToken} onClose={() => setShowRating(false)} />}
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────
const DeliveryPage = () => {
  const { token } = useParams();
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [msegatId, setMsegatId] = useState(null);
  const [accessToken, setAccessToken] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  if (!token) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" dir="rtl">
      <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md text-center">
        <AlertCircle size={40} className="mx-auto text-slate-400 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">الرابط غير صالح</h2>
        <p className="text-slate-500 text-sm">هذا الرابط غير صحيح أو منتهي الصلاحية.</p>
      </div>
    </div>
  );

  if (step === 'error') return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" dir="rtl">
      <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md text-center">
        <AlertCircle size={40} className="mx-auto text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">الرابط منتهي الصلاحية</h2>
        <p className="text-slate-500 text-sm">{errorMsg || 'يرجى العودة للتطبيق وإنشاء رابط جديد.'}</p>
      </div>
    </div>
  );

  if (step === 'phone') return <PhoneStep token={token} onSuccess={(p, id) => { setPhone(p); setMsegatId(id); setStep('otp'); }} />;
  if (step === 'otp') return <OTPStep token={token} phone={phone} msegatId={msegatId} onSuccess={at => { setAccessToken(at); setStep('gallery'); }} onBack={() => setStep('phone')} />;
  return <GalleryStep accessToken={accessToken} />;
};

export default DeliveryPage;
